import { useEffect, useRef } from "react";
import axios from "axios";
import {
  useMsal,
  useIsAuthenticated
} from "@azure/msal-react";
import {
  InteractionRequiredAuthError,
  InteractionStatus
} from "@azure/msal-browser";
import { protectedResources } from "../msalConfig";
import { useLoading } from '../context/LoadingContext';

export function useAxiosAuth() {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const interceptorSetRef = useRef(false);
  const popupInProgressRef = useRef(false);
  const { setLoading } = useLoading(); // <-- loading context

  // ✅ Use ref to read current inProgress without re-render
  const inProgressRef = useRef(inProgress);
  inProgressRef.current = inProgress;

  // 🔄 Helper: wait until MSAL interaction completes
  const waitForMsalReady = async (maxRetries = 5, delay = 1000) => {
    let attempt = 0;
    while (attempt < maxRetries) {
      if (inProgressRef.current === InteractionStatus.None) return true;
      console.warn(`MSAL busy (interaction in progress). Retrying in ${delay}ms...`);
      await new Promise((resolve) => setTimeout(resolve, delay));
      attempt++;
      delay *= 1.5; // exponential backoff
    }
    console.error("MSAL still busy after waiting. Giving up token request.");
    return false;
  };

  const addToken = async (resource) => {
    try {
      const response = await instance.acquireTokenSilent({
        scopes: resource.scopes,
        account: accounts[0],
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        if (popupInProgressRef.current) {
          console.warn("Popup already in progress — skipping duplicate popup.");
          return null;
        }
        popupInProgressRef.current = true;

        const ready = await waitForMsalReady();
        if (!ready) {
          popupInProgressRef.current = false;
          return null;
        }

        try {
          const response = await instance.acquireTokenPopup({
            scopes: resource.scopes,
            account: accounts[0],
          });
          return response.accessToken;
        } catch (popupError) {
          console.error("Popup login failed:", popupError);
          return null;
        } finally {
          popupInProgressRef.current = false;
        }
      } else {
        console.error("Silent token error:", error);
      }
      return null;
    }
  };

  // 🧩 Set up Axios interceptors once after login is stable
  useEffect(() => {
    if (!interceptorSetRef.current) {
      const reqInterceptor = axios.interceptors.request.use(async (config) => {
        setLoading(true); // <-- show loading
        // this logic should be applied only to protectedResources

        // --- JWT/CustomIDP authentication ---
        if (localStorage.getItem('jwt_authenticated') === 'true') {
          
          const resource = Object.values(protectedResources).find(
            (r) => config.url?.startsWith(r.endpoint)
          );
          if (resource) {
            const jwttoken = localStorage.getItem('jwttoken');
            const customidp = localStorage.getItem('customidp');
            const username = localStorage.getItem('jwt_username');
            console.log("Protected resource detected:", resource);

            if (jwttoken) {
              config.headers['Authorization'] = `JWT ${jwttoken}`;
            }
            if (username) {
              config.headers['username'] = username;
            }
            if (customidp) {
              config.headers['customidp'] = customidp;
            }
            config.headers["shva"] = localStorage.getItem("shva");
            config.headers["sourceName"] = "PowerBI UI";
          }
          setLoading(false); // <-- hide loading after config
          return config;
        }
        // --- MSAL authentication ---
        if (isAuthenticated && accounts.length > 0) {
          const ready = await waitForMsalReady();
          if (!ready) {
            setLoading(false); // <-- hide loading if not ready
            console.warn("MSAL not ready — skipping token injection.");
            return config;
          }
          const resource = Object.values(protectedResources).find(
            (r) => config.url?.startsWith(r.endpoint)
          );
          if (resource) {
            const token = await addToken(resource);
            if (token) config.headers.Authorization = `Bearer ${token}`;
            config.headers["shva"] = localStorage.getItem("shva");
            config.headers["sourceName"] = "PowerBI UI";
          }
          setLoading(false); // <-- hide loading after config
          return config;
        }
        setLoading(false); // <-- hide loading if rejected
        return Promise.reject(new Error("No valid authentication detected."));
      });

      const resInterceptor = axios.interceptors.response.use(
        (res) => {
          setLoading(false); // <-- hide loading on response
          return res;
        },
        (err) => {
          setLoading(false); // <-- hide loading on error
          return Promise.reject(err);
        }
      );

      interceptorSetRef.current = true;
      console.log("Axios interceptor set up for MSAL/JWT.");

      return () => {
        axios.interceptors.request.eject(reqInterceptor);
        axios.interceptors.response.eject(resInterceptor);
        interceptorSetRef.current = false;
        console.log("🧹 Axios interceptor removed.");
      };
    }
  }, [isAuthenticated, accounts, instance]);

  return axios;
}
